import { useMemo } from 'react'
import { GraphQL } from '@guestgoodie/graphql'
import { dateTime } from '@guestgoodie/helpers'
import * as Tooltip from '@radix-ui/react-tooltip'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  InformationSquareIcon,
  Tick01Icon
} from '@hugeicons/core-solid-rounded'
import { Trans } from '@lingui/react/macro'

import { useStateToLabel } from '../utils/helpers'
import { useMemberships } from '@/features/membership'
import { Suspense } from '@/components/ui/Suspense'
import { tooltipContainer } from '@/utils/popovers'
import Button from '@/components/ui/Button'
import { useMarkAsRead } from '../hooks/useMarkAsRead'

function Content({ order }: { order: GraphQL.OrderFragmentFragment }) {
  const members = useMemberships()

  const approvedUser = useMemo(() => {
    return members.find(member => member.userId === order.approvedById)?.user
  }, [order, members])

  const rejectedUser = useMemo(() => {
    return members.find(member => member.userId === order.rejectedById)?.user
  }, [order, members])

  const readByUser = useMemo(() => {
    return members.find(member => member.userId === order.readById)?.user
  }, [order, members])

  const start = useMemo(() => {
    if (order.__typename !== 'OrderDefault') return null
    if (!order.default.start) return null

    return dateTime.fullFormatter.format(new Date(order.default.start))
  }, [order])

  return (
    <div className="flex flex-col gap-2">
      {order.responseMessage && (
        <div className="flex flex-col">
          <span className="text-heading3">
            <Trans>Response Message</Trans>
          </span>
          <span>{order.responseMessage}</span>
        </div>
      )}
      {start && (
        <div className="flex flex-col">
          <span className="text-heading3">
            <Trans>Booked for time</Trans>
          </span>
          <span>{start}</span>
        </div>
      )}
      {approvedUser && (
        <div className="flex flex-col">
          <span className="text-heading3">
            <Trans>Approved by</Trans>
          </span>
          <span>
            {approvedUser.givenName} {approvedUser.lastName}
          </span>
        </div>
      )}
      {order.approvedAt && !approvedUser && (
        <div className="flex flex-col">
          <span className="text-heading3">
            <Trans>Approved by</Trans>
          </span>
          <span>
            <Trans>System</Trans>
          </span>
        </div>
      )}
      {order.approvedAt && (
        <div className="flex flex-col">
          <span className="text-heading3">
            <Trans>Approved at</Trans>
          </span>
          <span>
            {dateTime.fullFormatterCurrentTimezone.format(
              new Date(order.approvedAt)
            )}
          </span>
        </div>
      )}
      {rejectedUser && (
        <div className="flex flex-col">
          <span className="text-heading3">
            <Trans>Rejected by</Trans>
          </span>
          <span>
            {rejectedUser.givenName} {rejectedUser.lastName}
          </span>
        </div>
      )}
      {order.rejectedAt && !rejectedUser && (
        <div className="flex flex-col">
          <span className="text-heading3">
            <Trans>Rejected by</Trans>
          </span>
          <span>
            <Trans>System</Trans>
          </span>
        </div>
      )}
      {order.rejectedAt && (
        <div className="flex flex-col">
          <span className="text-heading3">
            <Trans>Rejected at</Trans>
          </span>
          <span>
            {dateTime.fullFormatterCurrentTimezone.format(
              new Date(order.rejectedAt)
            )}
          </span>
        </div>
      )}
      {readByUser && (
        <div className="flex flex-col">
          <span className="text-heading3">
            <Trans>Read by</Trans>
          </span>
          <span>
            {readByUser.givenName} {readByUser.lastName}
          </span>
        </div>
      )}
      {order.readAt && (
        <div className="flex flex-col">
          <span className="text-heading3">
            <Trans>Read at</Trans>
          </span>
          <span>
            {dateTime.fullFormatterCurrentTimezone.format(
              new Date(order.readAt)
            )}
          </span>
        </div>
      )}
    </div>
  )
}

export default function OrderStateWithInfo({
  order
}: {
  order: GraphQL.OrderFragmentFragment
}) {
  const stateToLabel = useStateToLabel()

  const { markAsRead, loading } = useMarkAsRead(order)

  const showInfo = useMemo(() => {
    return [GraphQL.OrderState.Approved, GraphQL.OrderState.Rejected].includes(
      order.state
    )
  }, [order])

  return (
    <div className="flex flex-col gap-2 items-center">
      <Tooltip.Root>
        <Tooltip.Trigger disabled={!showInfo}>
          <div className="flex flex-row items-center gap-1">
            <span className="text-regular text-text">
              {stateToLabel[order.state]}
            </span>
            <HugeiconsIcon
              icon={InformationSquareIcon}
              className="size-5 text-text"
            />
          </div>
        </Tooltip.Trigger>
        <Tooltip.Portal container={tooltipContainer}>
          <Tooltip.Content
            collisionPadding={20}
            className="flex flex-col gap-1 rounded-small bg-black/70 p-2 text-white z-tooltip"
            style={{
              maxWidth: 'var(--radix-tooltip-content-available-width)'
            }}
            sideOffset={5}
          >
            <Suspense>
              <Content order={order} />
            </Suspense>
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
      {!order.isRead && (
        // It is possible that something is shown as not read but as not pending as well, for example when canceled by the guest
        <Button Icon={Tick01Icon} onClick={markAsRead} loading={loading}>
          <Trans>Mark as Read</Trans>
        </Button>
      )}
    </div>
  )
}
